// Manager Sub-mode A first-response drafting UI — DESIGN §5.6.2, §10.1.2 #1;
// User Journeys & Service Design v2, Journey 01 (the tone-picker + single
// editable draft card).
//
// Calls `POST /api/cases/:id/maeve/first-response`, which returns all three
// CGC-signoff-gated tone variants (warm / formal / gentle) in one call. The UI
// shows ONE draft at a time — Warm pre-selected — with tone pills that switch
// between the pre-generated variants instantly (no re-fetch), per the design's
// "pick a tone, see the draft" flow. Per-tone edits are preserved when the
// manager switches tone.
//
// `autoGenerate` (used by the focused first-response page) fires the draft on
// mount so the manager sees a drafted message immediately ("show, don't ask" —
// principle 2). Embedded on the case-detail page it stays manual so we don't
// burn Vertex quota on every case open.
//
// The send buttons open the manager's own email / Teams / clipboard (Maeve
// never sends) and auto-stamp `first_response_sent_at` via the M3 setter
// (`POST /api/cases/:id/maeve/first-response/send`). Once stamped, this whole
// surface unmounts (the parent `['cases', id]` query refetches with the new
// timestamp).

import { useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { CaseSummary } from '@besideu/shared/cases';
import type { FirstResponseVariant, ToneVariant } from '@besideu/shared/maeve';
import type { ContactChannel, ContactPerson } from '@besideu/shared/preferences';
import { api, ApiError } from '@/lib/api.ts';
import { prefSentence } from '@/lib/format.ts';
import { Button } from './ui/Button.tsx';
import { DraftToneComposer } from './DraftToneComposer.tsx';

// §11.2 / §14.3 — the employee's preferred contact channel/person, surfaced so
// the manager honours it when they reach out.
const CHANNEL_HINT: Record<ContactChannel, string> = {
  email: 'prefers email',
  phone: 'prefers a phone call',
  teams: 'prefers Teams',
  text: 'prefers a text message',
  in_person: 'prefers to talk in person',
  none: 'has asked not to be contacted right now',
};
const PERSON_HINT: Record<Exclude<ContactPerson, 'unsure'>, string> = {
  manager: 'would like you to make contact',
  hr: 'would prefer HR to make contact',
  either: 'is happy for you or HR to make contact',
};

interface DraftResponse {
  variants: FirstResponseVariant[];
  promptVersionId: string;
}

export function ManagerMaeveFirstResponse({
  caseId,
  firstResponseSentAt,
  contactChannel,
  contactPerson,
  autoGenerate = false,
  onAnnounce,
}: {
  caseId: string;
  firstResponseSentAt: string | null;
  contactChannel?: ContactChannel | null;
  contactPerson?: ContactPerson | null;
  /** Draft on mount ("show, don't ask") — the focused first-response page. */
  autoGenerate?: boolean;
  /**
   * When provided, the "drafting…" status is reported here instead of
   * relying solely on its own `role="status"` paragraph — that paragraph's
   * implicit live region never announces while a `hidden` ancestor (e.g. an
   * inactive tab panel) hides it, silently dropping the notification. A
   * caller that can be tabbed away from (`ManagerCaseDetail`) lifts a
   * single shared, always-visible live region to the page level and feeds
   * it from here.
   */
  onAnnounce?: (message: string) => void;
}) {
  // Don't show the drafting UI after the manager has already sent.
  if (firstResponseSentAt !== null) return null;

  return (
    <DraftSurface
      caseId={caseId}
      contactChannel={contactChannel ?? null}
      contactPerson={contactPerson ?? null}
      autoGenerate={autoGenerate}
      onAnnounce={onAnnounce}
    />
  );
}

function DraftSurface({
  caseId,
  contactChannel,
  contactPerson,
  autoGenerate,
  onAnnounce,
}: {
  caseId: string;
  contactChannel?: ContactChannel | null;
  contactPerson?: ContactPerson | null;
  autoGenerate: boolean;
  onAnnounce?: ((message: string) => void) | undefined;
}) {
  const draft = useMutation({
    mutationFn: () => api.post<DraftResponse>(`/api/cases/${caseId}/maeve/first-response`),
  });

  // "Show, don't ask": on the focused page, draft on mount so a stressed
  // manager sees a message, not a blank box. Fire once. `draft.mutate` is
  // stable across renders (TanStack Query), so the empty-dep effect is safe.
  // `mutate` is stable across renders (TanStack Query); depend on `autoGenerate`
  // so this fires once on mount when the focused page requests an eager draft.
  const { mutate } = draft;
  useEffect(() => {
    if (autoGenerate) mutate();
  }, [autoGenerate, mutate]);

  // Deliberately omits `onAnnounce` from deps — callers rarely memoise it,
  // and re-firing this only when the pending/data transition actually
  // happens (not on every parent render) is the intended behaviour.
  useEffect(() => {
    if (!onAnnounce) return;
    if (draft.isPending) onAnnounce('Maeve is writing a first draft…');
    else if (draft.data) onAnnounce('Maeve has drafted a first response.');
  }, [draft.isPending, draft.data]);

  return (
    <section className="space-y-4 rounded-lg border border-border bg-card p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Draft a first response</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pick a tone, edit anything that doesn&rsquo;t feel right, then send it from your own
            account. Maeve suggests — you decide, and you send.
          </p>
        </div>
        {draft.data ? (
          <Button
            variant="secondary"
            onClick={() => draft.mutate()}
            loading={draft.isPending}
            className="py-2 text-sm"
          >
            Regenerate
          </Button>
        ) : null}
      </header>

      <ContactPreferenceHint channel={contactChannel ?? null} person={contactPerson ?? null} />

      {!draft.data && !draft.error && draft.isPending ? (
        <p className="text-sm text-muted-foreground" role={onAnnounce ? undefined : 'status'}>
          Maeve is writing a first draft…
        </p>
      ) : null}

      {!draft.data && !draft.error && !draft.isPending ? (
        <Button onClick={() => draft.mutate()}>Draft a response</Button>
      ) : null}

      {draft.error ? <DraftError error={draft.error} onRetry={() => draft.mutate()} /> : null}

      {draft.data ? (
        // Key on the mutation's submit timestamp so "Regenerate" remounts the
        // card — its `edits` state is seeded once from `variants`, so without a
        // fresh mount the new drafts wouldn't replace the old textareas.
        <TonePickerCard key={draft.submittedAt} caseId={caseId} variants={draft.data.variants} />
      ) : null}
    </section>
  );
}

// The employee's contact preference, shown above the draft so the manager sees
// it before choosing how to send. "No contact for now" gets an amber treatment.
function ContactPreferenceHint({
  channel,
  person,
}: {
  channel?: ContactChannel | null;
  person?: ContactPerson | null;
}) {
  const parts: string[] = [];
  if (channel) parts.push(CHANNEL_HINT[channel]);
  if (person && person !== 'unsure') parts.push(PERSON_HINT[person]);
  const sentence = prefSentence(parts);
  if (!sentence) return null;

  const noContact = channel === 'none';
  return (
    <p
      className={`rounded-md border p-3 text-sm ${
        noContact
          ? 'border-traffic-amber/40 bg-traffic-amber/10 text-foreground'
          : 'border-border bg-muted/40 text-muted-foreground'
      }`}
      role="note"
    >
      <span className="font-medium text-foreground">Their contact preference: </span>
      {sentence}
    </p>
  );
}

// Owns the ONE thing this surface has that the case-less drafter doesn't: the
// `first_response_sent_at` stamp. Everything visible — tone pills, edit
// buffer, send channels — is `DraftToneComposer`, shared with
// `ManagerDraftResponse`.
function TonePickerCard({
  caseId,
  variants,
}: {
  caseId: string;
  variants: FirstResponseVariant[];
}) {
  const queryClient = useQueryClient();

  // M3 Sub-mode A auto-setter. On success, invalidate the case query so
  // `firstResponseSentAt` flips to non-null and this surface unmounts.
  const send = useMutation({
    mutationFn: ({ tone, text }: { tone: ToneVariant; text: string }) =>
      api.post<{ case: CaseSummary }>(`/api/cases/${caseId}/maeve/first-response/send`, {
        tone,
        text,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cases', caseId] }),
  });

  return (
    <DraftToneComposer
      variants={variants}
      onCommit={(tone, text) => send.mutate({ tone, text })}
      commitPending={send.isPending}
      commitError={
        send.error ? (
          // Retry re-sends the variables of the failed attempt rather than
          // re-reading the composer's buffer — the manager may have kept
          // editing after the failure, and the stamp should record what they
          // actually sent.
          <SendError error={send.error} onRetry={() => send.mutate(send.variables)} />
        ) : null
      }
      confirmLabel="Use this draft"
      confirmTitle="Copies the draft to your clipboard and marks this support pathway's first response as sent"
    />
  );
}

function DraftError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail } = explainDraftError(error);
  return (
    <div className="space-y-3 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-4 text-sm">
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <Button variant="secondary" onClick={onRetry} className="py-1.5 text-sm">
        Try again
      </Button>
    </div>
  );
}

function explainDraftError(error: unknown): { headline: string; detail: string } {
  if (error instanceof ApiError) {
    if (error.status === 503 && error.message === 'maeve_unavailable') {
      return {
        headline: "Maeve isn't available right now",
        detail:
          'A besideU administrator needs to enable the Manager prompt before drafts can be generated. Please contact your HR team.',
      };
    }
    if (error.status === 502 && error.message === 'maeve_upstream_error') {
      return {
        headline: "Couldn't reach Maeve",
        detail: 'The drafting service is temporarily unavailable. Please try again in a moment.',
      };
    }
  }
  return {
    headline: 'Something went wrong',
    detail: 'Please try again, or refresh the page if the problem persists.',
  };
}

// Send-endpoint error surface. 4xx means the case state has moved (another tab
// stamped it, or the manager no longer owns the case) — refresh to resync.
function SendError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { headline, detail, isFatal } = explainSendError(error);
  return (
    <div className="space-y-2 rounded-md border border-traffic-red/40 bg-traffic-red/5 p-3 text-sm">
      <div>
        <p className="font-medium text-traffic-red">{headline}</p>
        <p className="mt-1 text-muted-foreground">{detail}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {!isFatal ? (
          <Button variant="secondary" onClick={onRetry} className="py-1.5 text-sm">
            Try again
          </Button>
        ) : (
          <Button
            variant="secondary"
            onClick={() => window.location.reload()}
            className="py-1.5 text-sm"
          >
            Refresh
          </Button>
        )}
      </div>
    </div>
  );
}

function explainSendError(error: unknown): { headline: string; detail: string; isFatal: boolean } {
  if (error instanceof ApiError) {
    if (error.status === 404) {
      return {
        headline: 'This support pathway is no longer available',
        detail:
          'It may have been removed, or another tab may have already marked the first response as sent. Refresh to see the latest state.',
        isFatal: true,
      };
    }
    if (error.status === 403) {
      return {
        headline: "You don't have access to this support pathway",
        detail: 'The support pathway may have been reassigned. Refresh to see the latest state.',
        isFatal: true,
      };
    }
  }
  return {
    headline: "Couldn't record the first response",
    detail:
      'Your draft is on your clipboard — send it from your email, then refresh this page and use the "I\'ve sent it" button if the stamp didn\'t go through.',
    isFatal: false,
  };
}
